/**
 * Bulk subject import.
 *
 * **Built at M8 and reachable only with `curl`.** The endpoint validates content type,
 * size, row count, field length and duplicates, and reports every rejected row with its
 * line number — none of which anybody could see, because there was no form.
 *
 * **The preview is a preview, not a second validator.** It shows the first rows as the
 * browser reads them so somebody can check they picked the right file and the columns line
 * up. It deliberately does not decide what is valid: re-implementing the parser here would
 * produce a client that disagrees with the server about which rows are acceptable, and the
 * disagreement would surface as rows silently missing rather than as an error.
 *
 * The uploaded bytes go through the authenticated client, so the request carries the bearer
 * token — an anchor or a bare `fetch` would not, which is the defect the download controls
 * had.
 */

import { useState, type ReactElement } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { upload } from '@/lib/api';
import type { ImportResult } from '@/lib/schemas';
import { Badge, Button, Card, ErrorState } from '@/components/ui';

/** How many rows the preview shows. Enough to check the columns, not enough to scroll. */
const PREVIEW_ROWS = 5;

/**
 * The CSV import panel for one case.
 *
 * @param props - The case receiving the subjects.
 * @returns The panel.
 */
export function SubjectImport({ caseId }: { caseId: string }): ReactElement {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string[]>([]);
  const [result, setResult] = useState<ImportResult | null>(null);

  const send = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('file', file as File);
      return upload<ImportResult>(`/cases/${caseId}/subjects:import`, form);
    },
    onSuccess: (body) => {
      setResult(body);
      setFile(null);
      setPreview([]);
      void queryClient.invalidateQueries({ queryKey: ['case-subjects', caseId] });
    },
  });

  async function onPick(chosen: File | null): Promise<void> {
    setResult(null);
    setFile(chosen);
    if (!chosen) {
      setPreview([]);
      return;
    }
    // Only the head of the file is read for the preview. A large upload is rejected by the
    // server on size; reading all of it here just to show five lines would be wasteful.
    const head = await chosen.slice(0, 4096).text();
    setPreview(head.split(/\r?\n/).filter(Boolean).slice(0, PREVIEW_ROWS + 1));
  }

  return (
    <Card title="Import subjects from CSV">
      <div className="space-y-3 p-4">
        <p className="text-xs text-muted">
          Two columns: <span className="font-mono">identifier_type,identifier_value</span>.
          The server validates every row and tells you which ones it refused and why —
          nothing is imported halfway on the strength of a guess here.
        </p>

        <input
          type="file"
          accept=".csv,text/csv"
          data-testid="import-file"
          onChange={(event) => void onPick(event.target.files?.[0] ?? null)}
          className="block text-sm"
        />

        {preview.length > 0 && (
          <div className="space-y-1">
            <div className="text-xs uppercase tracking-wide text-muted">
              First {Math.min(preview.length, PREVIEW_ROWS + 1)} lines, as read
            </div>
            <pre
              data-testid="import-preview"
              className="overflow-x-auto rounded border border-line bg-base p-2 font-mono text-xs"
            >
              {preview.join('\n')}
            </pre>
          </div>
        )}

        <Button
          data-testid="import-submit"
          tone="primary"
          disabled={!file || send.isPending}
          onClick={() => send.mutate()}
        >
          {send.isPending ? 'Importing…' : 'Import'}
        </Button>

        {send.error != null && <ErrorState error={send.error} />}

        {result && (
          <div className="space-y-2" data-testid="import-result">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={result.imported > 0 ? 'ok' : 'muted'}>
                {result.imported} imported
              </Badge>
              {result.errors.length > 0 && (
                <Badge tone="warn">{result.errors.length} refused</Badge>
              )}
              {result.truncated && <Badge tone="warn">truncated</Badge>}
            </div>

            {/* Rejected rows are listed, never summarised as a count. An import that
                quietly dropped rows produces an unexplained gap in the case. */}
            {result.errors.length > 0 && (
              <ul data-testid="import-errors" className="space-y-0.5">
                {result.errors.map((error) => (
                  <li key={error} className="font-mono text-xs text-warn">
                    {error}
                  </li>
                ))}
              </ul>
            )}

            {result.truncated && (
              <p className="text-xs text-warn">
                The file had more rows than one import accepts. The rows above were taken;
                split the remainder into a second file.
              </p>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
